import { useRef, useState, type FormEvent } from 'react';
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { Account } from '@/api/accounts';
import { ErrorCodes, isApiError, lineBeforeOpening } from '@/api/errors';
import { transactionsApi, type OwnerRegisterLine } from '@/api/transactions';
import { toCivil } from '@/features/envelope/window';
import { formatCivilDate, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { applyBackendFields, validationAlert } from '@/features/shared/useFieldErrors';
import { allocationErrorMessage } from './allocationErrors';
import { SubmitButton } from '@/components/SubmitButton';
import { GuardedDialog } from '@/components/GuardedDialog';
import { useDialogDirtyGuard } from '@/shared/useDialogDirtyGuard';
import { useDirty } from '@/shared/useDirty';

const TITLE_ID = 'manual-txn-title';
const MANUAL_FIELDS = ['postedOn', 'amountMinor', 'payee', 'memo'] as const;
type ManualField = (typeof MANUAL_FIELDS)[number];

interface ManualTransactionDialogProps {
  account: Account;
  onClose: () => void;
  onCreated: (line: OwnerRegisterLine) => void;
  onSubscriptionRequired: () => void;
}

/**
 * Owner-only manual transaction (TMMAPP-0008): date (default today), signed amount via a
 * deposit/withdrawal toggle (the field takes the magnitude), payee required, memo optional.
 */
export function ManualTransactionDialog({
  account,
  onClose,
  onCreated,
  onSubscriptionRequired,
}: ManualTransactionDialogProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [direction, setDirection] = useState<'deposit' | 'withdrawal'>('deposit');
  const [postedOn, setPostedOn] = useState(() => toCivil(new Date()));
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [memo, setMemo] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<ManualField, string>>>({});
  const [amountError, setAmountError] = useState(false);
  const [payeeError, setPayeeError] = useState(false);
  const payeeRef = useRef<HTMLInputElement>(null);
  const currency = account.currency ?? account.operatingCurrency;
  const { dirty } = useDirty({ direction, postedOn, amount, payee, memo });
  const guard = useDialogDirtyGuard(dirty, onClose, submitting);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    const mag = parseMoneyToMinor(amount, currency, locale);
    const trimmed = payee.trim();
    setAmountError(mag === null || mag === 0);
    setPayeeError(trimmed.length === 0);
    if (mag === null || mag === 0 || trimmed.length === 0) {
      guard.focusErrors();
      return;
    }
    const amountMinor = direction === 'withdrawal' ? -Math.abs(mag) : Math.abs(mag);
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    try {
      const line = await transactionsApi.createManual(account.id, {
        postedOn,
        amountMinor,
        payee: trimmed,
        ...(memo.trim().length > 0 ? { memo: memo.trim() } : {}),
      });
      onCreated(line);
      onClose();
    } catch (err) {
      const beforeOpening = lineBeforeOpening(err);
      if (isApiError(err) && err.code === ErrorCodes.SUBSCRIPTION_REQUIRED) {
        onSubscriptionRequired();
      } else if (isApiError(err) && err.code === ErrorCodes.VALIDATION_FAILED) {
        const next: Partial<Record<ManualField, string>> = {};
        const result = applyBackendFields(
          err,
          MANUAL_FIELDS,
          (field, message) => {
            next[field] = message;
          },
          t,
          guard.focusErrors,
        );
        setFieldErrors(next);
        setError(validationAlert(result, err, t, t('accounts.errorValidation')));
      } else if (beforeOpening !== null) {
        // Already in the opening balance (TMMAPI-0196 U3; TMMAPP-0166 item 4), with the opening date.
        setError(
          beforeOpening.openingOn !== null
            ? t('accounts.errorBeforeOpeningOn', {
                date: formatCivilDate(beforeOpening.openingOn, locale),
              })
            : t('accounts.errorBeforeOpening'),
        );
        guard.focusErrors();
      } else {
        setError(allocationErrorMessage(err, t, currency, locale) ?? t('accounts.errorGeneric'));
        guard.focusErrors();
      }
      setSubmitting(false);
    }
  }

  return (
    <GuardedDialog
      guard={guard}
      titleId={TITLE_ID}
      fullWidth
      maxWidth="sm"
      slotProps={{ transition: { onEntered: () => payeeRef.current?.focus() } }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <DialogTitle id={TITLE_ID}>{t('accounts.manualTitle')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {error !== null && <Alert severity="error">{error}</Alert>}
            <TextField
              type="date"
              label={t('accounts.manualPostedOn')}
              value={postedOn}
              onChange={(event) => setPostedOn(event.target.value)}
              required
              fullWidth
              error={fieldErrors.postedOn !== undefined}
              helperText={fieldErrors.postedOn}
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormControl>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={direction}
                onChange={(_event, value: 'deposit' | 'withdrawal' | null) => {
                  if (value !== null) setDirection(value);
                }}
                aria-label={t('accounts.manualAmount')}
              >
                <ToggleButton value="deposit">{t('accounts.deposit')}</ToggleButton>
                <ToggleButton value="withdrawal">{t('accounts.withdrawal')}</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>
            <TextField
              label={t('accounts.manualAmount')}
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
              required
              fullWidth
              error={amountError || fieldErrors.amountMinor !== undefined}
              helperText={fieldErrors.amountMinor}
            />
            <TextField
              label={t('accounts.manualPayee')}
              value={payee}
              onChange={(event) => setPayee(event.target.value)}
              inputRef={payeeRef}
              required
              fullWidth
              error={payeeError || fieldErrors.payee !== undefined}
              helperText={fieldErrors.payee}
            />
            <TextField
              label={t('accounts.manualMemo')}
              value={memo}
              onChange={(event) => setMemo(event.target.value)}
              fullWidth
              error={fieldErrors.memo !== undefined}
              helperText={fieldErrors.memo}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={guard.requestClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" loading={submitting}>
            {t('accounts.manualSubmit')}
          </SubmitButton>
        </DialogActions>
      </form>
    </GuardedDialog>
  );
}
